import { isValidElement, useCallback, useEffect, useRef, useState } from 'react';
import { ArrowHead } from '../../../assets/images/components/NudosComponents';
import { IElement } from '../../../types/global';
import { selectDropdownVariants } from './BordSelectDropdown.type';
import './BordSelectDropdown.scss';

/**
 * React functional component corresponding to Nudos select dropdown input.
 * @property {JSX.Element[]} selectOptions - Required, the options to be displayed in the dropdown select. Each option should have the changeSelectedOption as onClick callback.
 * IMPORTANT: each of the options should have the className "optionContainer" to inherit the default styles for the options.
 * @property {string | JSX.Element} currentValueOrplaceholder - Required, the current value or placeholder of the select input when the dropdown is closed, usually the selected option or the selected option string value, or a placeholder if no option has been selected.
 * *@property {boolean} isFilled - Required, boolean indicating if an option has been selected and thus the select input is filled. Should be passed to change the style from empty to filled. Its value should reflect the change in the input value. Default is false.
 * @property {string | JSX.Element} label - Optional, a label for the select input.
 * @property {string} errorText - Optional, the text to be shown if theres an error with the select input form. If an error is passed the style of the component will change to reflect that.
 * @property {boolean} isDeactivated - Optional, boolean indicating if the select input is deactivated. If a value is provided the input will behave as a read only field and styles will be accordingly.
 * @property {string} componentSize - Optional, one of the standard sizes for the components, corresponding to one of the following words: "small", "medium" or "large". If no value is passed the component will have the width of its parent container.
 * @property {boolean} closesOnClickOutside - Optional, a boolean indicating if the dropdown closes when clicked outside. If no value is pause the default is true, with the select value being updated to the default.
 * @property {boolean} closesOnChangeSelection - Optional, a boolean indicating if the dropdown closes when the selected option(s) change. If no value is pause the default is true.
 * @property {string} selectClassName - Optional, class name for the select input to be use in customizing styles.
 * @property {boolean} isOptionsContainerFullHeight - Optional, boolean indicating if the options container dropdown should be full height.
 * @property {boolean} hideErrorText - Optional, if the dropdown is part of another component that shows an error text, used this boolean to prevent from repeating the error text.
 * @property {boolean} hideIcon - Optional, use it to hide the arrow on the right side of the component.
 * @property {boolean} isLoading - Optional, boolean indicating if the input is loading and therefore the skeleton loader is to be shown instead of the input field. Default is false.
 * @property {"default" | "compact"} variant - Optional, define el estilo del dropdown. "default" muestra el dropdown con border completo y layout normal. "compact" muestra el dropdown sin border con el valor y las flechas juntas del lado derecho. Default es "default".
 * @returns
 */
const BordSelectDropdown = ({
  selectOptions,
  currentValueOrplaceholder,
  isFilled,
  label,
  errorText,
  isDeactivated,
  componentSize,
  closesOnClickOutside = true,
  closesOnChangeSelection = true,
  selectClassName,
  isOptionsContainerFullHeight,
  hideErrorText,
  hideIcon,
  isLoading,
  variant = 'default',
  customClassNameOptionsContainer
}: IBordSelectDropdown) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const refContainer = useRef<HTMLDivElement>(null);
  const handleClickSelect = () => {
    if (isDropdownOpen) {
      !isDeactivated && closesOnChangeSelection && setIsDropdownOpen(false);
    } else {
      !isDeactivated && setIsDropdownOpen(true);
    }
  };
  const handleCloseDropDown = () => !isDeactivated && setIsDropdownOpen(false);

  const onHandleClickAway = useCallback(
    async (e: IElement) => {
      if (refContainer.current && !refContainer.current.contains(e.target)) {
        closesOnClickOutside && handleCloseDropDown();
      }
    },
    [refContainer]
  );

  useEffect(() => {
    window.addEventListener('mousedown', onHandleClickAway);
    return () => {
      window.removeEventListener('mousedown', onHandleClickAway);
    };
  }, [onHandleClickAway]);

  if (isLoading) {
    return (
      <div className={`nudosSelectDropdownSkeletonLoader animationLoader ${componentSize ? componentSize : ''}`}>
        {label && <div className="selectLabel">{label}</div>}
        <div className="selectSkeletonLoader animationLoader" />
      </div>
    );
  }

  return (
    <div
      className={`nudosSelectDropdown ${selectClassName ? selectClassName : ''} ${isDeactivated ? 'deactivated' : ''} ${
        isFilled || isDropdownOpen ? 'filled' : ''
      } ${!!errorText?.length ? 'error' : ''} ${componentSize ? componentSize : ''} ${variant}`}
    >
      {label && typeof label === 'string' && <div className="selectLabel">{label}</div>}
      {label && typeof label !== 'string' && isValidElement(label) && label}
      <div className="selectContainer" onClick={handleClickSelect}>
        <div className="closedSelectInput">
          <div className="currentValueOrplaceholder">{currentValueOrplaceholder}</div>
          {!hideIcon && !isDropdownOpen && (
            <div className="expandArrow">
              <ArrowHead />
            </div>
          )}
          {!hideIcon && isDropdownOpen && (
            <div className="contractArrow">
              <ArrowHead />
            </div>
          )}
        </div>
        {isDropdownOpen && !isDeactivated && (
          <div
            ref={refContainer}
            className={`optionsContainer ${
              isOptionsContainerFullHeight ? 'fullHeight' : ''
            } ${customClassNameOptionsContainer}`}
          >
            <div className="paddingWrapper">{selectOptions}</div>
          </div>
        )}
      </div>
      {!!errorText && !hideErrorText && !isDeactivated && <div className="errorText">{errorText}</div>}
    </div>
  );
};

export default BordSelectDropdown;

export interface IBordSelectDropdown {
  selectOptions: JSX.Element[];
  currentValueOrplaceholder: string | JSX.Element;
  isFilled: boolean;
  label?: string | JSX.Element;
  errorText?: string;
  isDeactivated?: boolean;
  componentSize?: string;
  closesOnClickOutside?: boolean;
  closesOnChangeSelection?: boolean;
  selectClassName?: string;
  isOptionsContainerFullHeight?: boolean;
  hideErrorText?: boolean;
  hideIcon?: boolean;
  isLoading?: boolean;
  variant?: selectDropdownVariants;
  customClassNameOptionsContainer?: string;
}
